import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Sparkles } from "lucide-react";
import { useRecommendations } from "./RecommendationsContext";
import { packPages } from "./packPages";

export interface RecommendationChipsProps {
  className?: string;
  /** Optional row header (e.g. "Suggested next steps"). */
  label?: ReactNode;
  labelClassName?: string;
  chipClassName?: string;
  /**
   * Fallback chips-per-page when layout measurement isn't available
   * (SSR, test environments). In the browser, pages pack by measurement:
   * each page holds exactly what 2 rows fit at the current width.
   */
  pageSize?: number;
}

/**
 * The recommendation row — part of the chat widget, rendered above the
 * composer, ALWAYS present (the registry is static; no ranker, no state
 * channel). Compact pills, hard-capped at 2 rows; longer lists paginate
 * with big circular arrows flanking the rows (a scrollbar here is
 * undiscoverable) and a "1 / 3" counter in the header.
 *
 * Chips with a panel (bespoke component or collects fields) show a chevron
 * and toggle the composer transform; prompt chips PREFILL the chat input
 * with their text — the user edits and sends it themselves, never an
 * auto-send.
 */
export function RecommendationChips({
  className,
  label,
  labelClassName,
  chipClassName,
  pageSize = 6,
}: RecommendationChipsProps) {
  const {
    recommendations,
    active,
    open,
    close,
    prefillPrompt,
    hasPanel,
    descriptorFor,
    iconFor,
  } = useRecommendations();
  const [page, setPage] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  // Measured page boundaries (indices into recommendations); null = no
  // layout available yet → count-based pageSize fallback.
  const [packed, setPacked] = useState<number[][] | null>(null);
  // Unmounts the measurer in zero-layout environments (happy-dom, SSR) so
  // its inert pills don't shadow the visible ones in DOM queries.
  const [measurable, setMeasurable] = useState(true);

  // Pack pages to what the container actually fits: measure every chip's
  // intrinsic width off the hidden measurer, then greedily fill 2 rows per
  // page. Re-packs on container resize and when the list changes.
  useLayoutEffect(() => {
    const compute = () => {
      const wrap = wrapRef.current;
      const meas = measureRef.current;
      if (!wrap || !meas) return setPacked(null);
      const width = wrap.clientWidth;
      const widths = Array.from(meas.children).map(
        (c) => (c as HTMLElement).offsetWidth
      );
      // happy-dom/SSR report zero layout — keep the count fallback and
      // drop the measurer entirely.
      if (!width || widths.every((w) => !w)) {
        setPacked(null);
        setMeasurable(false);
        return;
      }
      setPacked(packPages(widths, width, { rowsPerPage: 2 }));
    };
    compute();
    if (typeof ResizeObserver === "undefined" || !wrapRef.current) return;
    const ro = new ResizeObserver(compute);
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, [recommendations]);

  if (recommendations.length === 0) return null;

  const fallbackPages = Array.from(
    { length: Math.ceil(recommendations.length / pageSize) },
    (_, i) => recommendations.slice(i * pageSize, (i + 1) * pageSize)
  );
  // `packed` is measured in a layout effect (post-render), so on the render
  // where `recommendations` shrinks it can still hold stale, now-out-of-range
  // indices. Drop those (and any pages left empty) instead of surfacing
  // `undefined` chips — the effect re-packs correctly on the next tick.
  const pages = packed
    ? packed
        .map((idxs) =>
          idxs
            .map((i) => recommendations[i])
            .filter((r): r is (typeof recommendations)[number] => r != null)
        )
        .filter((p) => p.length > 0)
    : fallbackPages;
  const pageCount = pages.length;
  const currentPage = Math.min(page, pageCount - 1);
  const visible = pages[currentPage] ?? [];
  const paginated = pageCount > 1;

  // One source for pill geometry — the hidden measurer must produce the
  // exact widths the visible pills render at, or packing drifts.
  const pillClasses = (isActive: boolean) =>
    twMerge(
      "inline-flex h-8 max-w-full items-center gap-1.5 rounded-[10px] border px-3",
      "text-[13px] font-medium transition-colors",
      isActive
        ? "border-primary-300 bg-primary-50 text-primary-700"
        : "border-neutral-200 bg-white text-neutral-800 hover:border-neutral-300 hover:bg-neutral-50",
      chipClassName,
    );

  const pillContent = (rec: (typeof recommendations)[number], isActive: boolean) => {
    const panel = hasPanel(rec);
    const icon = descriptorFor(rec)?.icon ?? iconFor?.(rec);
    return (
      <>
        {icon && <span className="shrink-0 text-neutral-500 [&>svg]:size-3.5">{icon}</span>}
        <span className="truncate whitespace-nowrap">{rec.text}</span>
        {panel &&
          (isActive ? (
            <ChevronUp className="size-3 opacity-50" />
          ) : (
            <ChevronDown className="size-3 opacity-50" />
          ))}
      </>
    );
  };

  return (
    <div data-testid="recommendation-chips" className={twMerge("relative space-y-1.5", className)}>
      {(label || paginated) && (
        <div className="flex items-center justify-between">
          <div
            className={twMerge(
              "inline-flex items-center gap-1.5 font-mono text-[11px] font-medium uppercase tracking-widest text-neutral-400",
              labelClassName,
            )}
          >
            {label && <Sparkles className="size-3.5 text-orange-400" />}
            {label}
          </div>
          {paginated && (
            <div
              className="font-mono text-[13px] tracking-widest text-neutral-400"
              data-testid="recommendation-pages"
              aria-label={`Suggestions page ${currentPage + 1} of ${pageCount}`}
            >
              {currentPage + 1} / {pageCount}
            </div>
          )}
        </div>
      )}

      {/* Big circular arrows FLANK the rows — pagination has to be visible
          at a glance (a scrollbar here is undiscoverable). The wrap sits
          between them; ResizeObserver re-packs to the narrowed width. */}
      <div className="flex items-center gap-2">
        {paginated && (
          <PagerArrow
            direction="previous"
            disabled={currentPage === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          />
        )}
        {/* Hard cap: exactly 2 rows. Pills are fixed-height (h-8 = 32px),
            so 2 rows + gap = 70px and a 3rd row starts at 76px — the 72px
            clamp shows two full rows and hides a 3rd entirely, never
            slicing a pill mid-height. pageSize keeps pages under this in
            practice; when a narrow container wraps a page past two rows
            anyway, the window scrolls instead of stranding chips
            unreachable behind a clip. */}
        <div
          ref={wrapRef}
          className="flex max-h-[72px] flex-1 content-start flex-wrap justify-center gap-1.5 overflow-y-auto overflow-x-hidden"
        >
          {visible.map((rec) => {
            const panel = hasPanel(rec);
            const isActive = active?.recommendationId === rec.recommendationId;

            return (
              <button
                key={rec.recommendationId}
                type="button"
                aria-expanded={panel ? isActive : undefined}
                className={pillClasses(isActive)}
                onClick={() => {
                  if (!panel) {
                    prefillPrompt(rec);
                  } else if (isActive) {
                    close();
                  } else {
                    open(rec);
                  }
                }}
              >
                {pillContent(rec, isActive)}
              </button>
            );
          })}
        </div>
        {paginated && (
          <PagerArrow
            direction="next"
            disabled={currentPage === pageCount - 1}
            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
          />
        )}
      </div>

      {/* Hidden measurer: every chip at intrinsic width, feeding packPages.
          Same pill classes/content as the visible row, rendered inert. */}
      {measurable && (
      <div
        ref={measureRef}
        aria-hidden
        className="pointer-events-none invisible absolute -z-10 flex h-0 w-full flex-nowrap gap-1.5 overflow-hidden"
      >
        {recommendations.map((rec) => (
          <span key={rec.recommendationId} className={pillClasses(false)}>
            {pillContent(rec, false)}
          </span>
        ))}
      </div>
      )}
    </div>
  );
}

function PagerArrow({
  direction,
  disabled,
  onClick,
}: {
  direction: "previous" | "next";
  disabled: boolean;
  onClick: () => void;
}) {
  const Chevron = direction === "previous" ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      aria-label={direction === "previous" ? "Previous suggestions" : "More suggestions"}
      disabled={disabled}
      onClick={onClick}
      className="inline-flex size-11 shrink-0 items-center justify-center rounded-full border border-neutral-200
                 bg-white text-neutral-500 shadow-sm transition-colors hover:border-neutral-300 hover:bg-neutral-50
                 hover:text-neutral-700 disabled:pointer-events-none disabled:opacity-30"
    >
      <Chevron className="size-5" strokeWidth={2} />
    </button>
  );
}
